import {
  ReportService,
  StatisticService,
  InitWithAmplify,
  InitEnvironmentVariable,
  ClassMonthlyReportResult,
  StudentStatisticsResult,
  StudentMonthlyReportService
} from '@PikaDoApp/npm-pikado-logic';
import {
  StudentReport,
  StatisticsByStudentResponse,
  OverAllPerformanceResponse,
  GenerateClassMonthlyReportInput,
} from './types/NewStudentReportTypes';
import {store} from '../../../redux/store';
import {storeStudentMonthlyReportByMonth} from '../../../redux/actions/fullReport';
import {Amplify} from 'aws-amplify';
import moment from 'moment';
import {getEnvironment} from '../../../lib/getEnvironment';
import {
  createDefaultStudentReport,
  createDefaultStatisticByStudent,
} from './defaultStudentReportData';

/**
 * Service wrapper for the new student report system from npm-pikado-logic
 * This service fetches data from the new API and will be used alongside
 * the converter to transform data for the existing UI
 */
export class NewStudentReportService {
  private static isInitialized = false;

  private reportService: ReportService;
  private statisticService: StatisticService;
  private studentMonthlyReportService: StudentMonthlyReportService
  private readonly TIMEZONE = 'Africa/Cairo';
  private readonly CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 hours (same as old system)

  /**
   * Initialize npm-pikado-logic with Amplify configuration
   * This must be called AFTER Amplify.configure() in your app
   * It's safe to call multiple times - initialization only happens once
   * @param awsConfig - Optional raw AWS config object for Amplify v6 compatibility
   */
  static initialize(awsConfig?: any) {
    if (NewStudentReportService.isInitialized) {
      return;
    }


    // Try passing the raw config if provided (for Amplify v6)
    if (awsConfig) {
      InitWithAmplify(awsConfig);
    } else {
      InitWithAmplify(Amplify);
    }

    InitEnvironmentVariable(getEnvironment());

    NewStudentReportService.isInitialized = true;
  }

  constructor() {
    this.reportService = new ReportService();
    this.statisticService = new StatisticService();
    this.studentMonthlyReportService = new StudentMonthlyReportService()
  }

  /**
   * Generate monthly report for a class (triggers backend processing)
   * @param input - GenerateClassMonthlyReportInput
   * @returns ClassMonthlyReportResult or null
   */
  async generateClassMonthlyReport(
    input: GenerateClassMonthlyReportInput,
  ): Promise<ClassMonthlyReportResult | null> {
    try {
      return await this.reportService.generateClassMonthlyReport(input);
    } catch (error) {
      console.error('❌ Error in generateClassMonthlyReport:', error);
      throw error;
    }
  }

  /**
   * Get all monthly reports for a student
   * @param studentId - Student ID
   * @returns Array of StudentReport objects
   */
  async getAllStudentReports(studentId: string): Promise<StudentReport[]> {
    try {
      const reports = await this.reportService.getAllStudentReports(studentId);
      return reports?.length > 0 ? reports : [createDefaultStudentReport(studentId)];
    } catch (error) {
      console.error('❌ Error in getAllStudentReports:', error);
      return [createDefaultStudentReport(studentId)];
    }
  }

  /**
   * Get statistics by student ID (includes phonetics & syllables)
   * @param studentId - Student ID
   * @returns StatisticsByStudentResponse array
   */
  async getStudentStatisticsByStudentId(
    studentId: string,
  ): Promise<StudentStatisticsResult> {
    try {
      const stats = await this.statisticService.getStudentStatisticsByStudentId(studentId)
      console.log(stats, "Abla stats")
      return stats
    } catch (error) {
      console.error('❌ Error in getStatisticsByStudentId:', error);
      return error
    }
  }

  /**
   * Get overall performance for a student
   * @param studentId - Student ID
   * @param timezone - Timezone string (e.g., 'Asia/Riyadh')
   * @returns OverAllPerformanceResponse or null
   */
  async getOverAllPerformance(
    studentId: string,
    timezone: string,
  ): Promise<OverAllPerformanceResponse | null> {
    try {
      const result = await this.statisticService.getOverAllPerformance(
        studentId,
        timezone,
      );
      return result as OverAllPerformanceResponse | null;
    } catch (error) {
      console.error('❌ Error in getOverAllPerformance:', error);
      throw error;
    }
  }

  /**
   * Get student monthly report by studentId and month with caching
   * Implements caching strategy: cache past months, always fetch current month fresh
   * @param studentId - Student ID
   * @param month - Month in YYYY-MM format
   * @returns Student monthly report or null
   */
  async getStudentMonthlyReportByStudentIdAndMonth(
    studentId: string,
    month: string
  ): Promise<any | null> {
    try {
      const cacheKey = `${studentId}-${month}`;
      const currentMonth = moment().format('YYYY-MM');
      const isCurrentMonth = month === currentMonth;

      // NEVER use cache for current month - always fetch fresh
      if (!isCurrentMonth) {
        const state = store.getState();
        const cached = state.fullReport?.studentMonthlyReportsByMonth?.[cacheKey];
        if (cached) {
          console.log(`✅ Returning cached student monthly report for: ${cacheKey}`);
          return cached;
        }
      } else {
        console.log(`🔄 Current month (${month}) - skipping cache, fetching fresh data`);
      }

      // Fetch fresh data
      console.log(`🌐 Fetching student monthly report from API: ${cacheKey}`);
      const reportData = await this.studentMonthlyReportService.getStudentMonthlyReportByStudentIdAndMonth(
        studentId,
        month
      );

      // Cache ONLY if it's NOT the current month
      if (!isCurrentMonth && reportData) {
        console.log(`💾 Caching student monthly report for past month: ${cacheKey}`);
        store.dispatch(storeStudentMonthlyReportByMonth(studentId, month, reportData));
      }

      return reportData;
    } catch (error) {
      console.error('❌ Error in getStudentMonthlyReportByStudentIdAndMonth:', error);
      throw error;
    }
  }
}
